<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type LxcSideCanvas from "@lxc/app-device-common/src/components/LxcSideCanvas.vue";
import type LxcForm from "@lxc/app-device-common/src/components/form/LxcForm.vue";
import type { Rules } from "async-validator";
import type { InternalRuleItem } from "async-validator/dist-types/interface";
import dayjs from "dayjs";
import { storeToRefs } from "pinia";
import { useFetchContents } from "~/core/composables/useFetchContents";
import { SearchMode } from "~/core/composables/useSearch";
import type { ContentResource } from "~/core/models/ContentResource.interface";
import { ContentTags } from "~/core/models/ContentTags";
import type LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { Filters } from "~/core/models/filters";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useCreateOperationConfiguration } from "~/modules/dtwin/composables/useCreateOperationConfiguration";
import { useUpdateOperationConfiguration } from "~/modules/dtwin/composables/useUpdateOperationConfiguration";
import type { DtwinCreateOperation } from "~/modules/dtwin/models/DtwinCreateOperation.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import type { DtwinUpdateOperation } from "~/modules/dtwin/models/DtwinUpdateOperation.interface";
import { OperationCreationMode } from "~/modules/dtwin/models/OperationCreationMode.enum";
import { OperationScheduler } from "~/modules/dtwin/models/OperationScheduler.enum";
import { OperationServiceOrigin } from "~/modules/dtwin/models/OperationServiceOrigin.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type { DtwinOperationForm } from "~/types";
import { getCustomInputParameters } from "~/utils/getCustomInputParameters";

const props = defineProps<{
  show: boolean;
  dtwinUid: string;
  dtwinOperationModel: OperationModel;
  operation?: DtwinOperation;
  reloadConfigurationList?: boolean;
}>();

const emits = defineEmits([
  "update:show",
  "change",
  "update:reloadConfigurationList",
]);

const { t } = useI18n();
const serialize = useSerialize();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const {
  isLoading: isLoadingConfigurations,
  error: errorConfigurations,
  allResults: configurations,
  fetchAllData: fetchConfigurations,
  setFilter,
} = useFetchContents({
  organizationCode: currentOrganization.value?.code,
  searchMode: SearchMode.FILTER_SEARCH,
});
const {
  isLoading: isLoadingCreateOperation,
  error: errorCreateOperation,
  createOperation,
} = useCreateOperationConfiguration({
  organizationCode: currentOrganization.value?.code,
});
const {
  isLoading: isLoadingUpdateOperation,
  error: errorUpdateOperation,
  updateOperation,
} = useUpdateOperationConfiguration({
  organizationCode: currentOrganization.value?.code,
});

const showSideCanvas = computed({
  get() {
    return props.show;
  },
  set(value) {
    emits("update:show", value);
  },
});

const configurationUpdateFormRef: Ref<typeof LxcForm | undefined> = ref();
const sideCanvasRef: Ref<typeof LxcSideCanvas | undefined> = ref();

async function loadData() {
  setFilter(Filters.DTWIN_UID, props.dtwinUid);
  setFilter(Filters.CONTENT_TAGS, [
    ContentTags.CONFIGURATION,
    ContentTags.DEVICE,
  ]);
  if (fetchConfigurations) {
    await fetchConfigurations();
  }
}

const defaultConfigurationUpdateForm: DtwinOperationForm = {
  deviceTwinUid: props.dtwinUid,
  launchPlannedAt: undefined,
  configurationUid: undefined,
  startsAtCreation: false,
};
const configurationUpdateForm: DtwinOperationForm = reactive({
  ...defaultConfigurationUpdateForm,
});
const initialFormSerialized: Ref<string> = ref(
  serialize(configurationUpdateForm),
);

const formHasChanged: ComputedRef<boolean> = computed(
  () => serialize(configurationUpdateForm) !== initialFormSerialized.value,
);

const selectedConfiguration: ComputedRef<ContentResource | undefined> =
  computed(() => {
    return configurations?.value?.find(
      (configuration) =>
        configuration.uid === configurationUpdateForm.configurationUid,
    );
  });

async function setOperationDetailForm(dtwinOperation?: DtwinOperation) {
  Object.assign(
    configurationUpdateForm,
    dtwinOperation
      ? {
          deviceTwinUid: dtwinOperation.deviceTwinUid,
          launchPlannedAt: new Date(dtwinOperation.launchPlannedAt),
          configurationUid:
            dtwinOperation.customInputParameters?.specificConfigUid,
        }
      : { ...defaultConfigurationUpdateForm },
  );

  initialFormSerialized.value = serialize(configurationUpdateForm);

  // set timeout to be sure that the clearValidate is processed after updating the form inputs
  setTimeout(() => {
    configurationUpdateFormRef.value?.clearValidate();
  }, 0);
}

const defaultStartDate = new Date(Date.now());
const isPlannedLaunchDateValid = (
  _: InternalRuleItem,
  value: Date,
  callback: (error?: Error) => void,
) => {
  if (value) {
    const valueDate = new Date(value);
    if (valueDate.getTime() < defaultStartDate.getTime()) {
      callback(new Error(t("operation.dateInferiorToToday")));
    }
  }
  callback();
};

const rules: Rules = {
  launchPlannedAt: [
    {
      required: true,
      message: t("input.error.required"),
    },
    {
      validator: isPlannedLaunchDateValid,
    },
  ],
  configurationUid: [
    {
      required: true,
      message: t("input.error.required"),
    },
  ],
};

const isFormValid: ComputedRef<boolean | undefined> = computed(
  () => configurationUpdateFormRef.value?.isValid,
);

const isConfirmDisabled: ComputedRef<boolean> = computed(() => {
  return (
    !isFormValid.value ||
    isLoadingConfigurations.value ||
    isLoadingCreateOperation.value ||
    isLoadingUpdateOperation.value ||
    !selectedConfiguration.value ||
    !formHasChanged.value
  );
});

const onClose = () => {
  showSideCanvas.value = false;
  setOperationDetailForm();
};

const onCancel = () => {
  sideCanvasRef.value?.hide();
};

const onSubmit = async () => {
  if (isFormValid.value) {
    const customInputParameters = await getCustomInputParameters({
      operationModel: props.dtwinOperationModel,
      configuration: selectedConfiguration.value,
    });
    const error: Ref<LxcError | undefined> = ref();

    // Update operation
    if (props.operation) {
      const updateOperationPayload: DtwinUpdateOperation = {
        launchPlannedAt: dayjs(configurationUpdateForm.launchPlannedAt)
          .utc()
          .toISOString(),
        customInputParameters,
        expiredAt: dayjs(configurationUpdateForm.launchPlannedAt)
          .add(1, "month")
          .utc()
          .toISOString(),
      };
      await updateOperation(props.operation.uid, updateOperationPayload);

      if (errorUpdateOperation.value) {
        error.value = errorUpdateOperation.value;
      }
    }
    // Create operation
    else {
      const createOperationPayload: DtwinCreateOperation = {
        modelUid: props.dtwinOperationModel.uid,
        deviceTwinUid: props.dtwinUid,
        serviceOrigin: OperationServiceOrigin.UNITARY,
        creationMode: OperationCreationMode.MANUAL,
        launchPlannedAt: dayjs(configurationUpdateForm.launchPlannedAt)
          .utc()
          .toISOString(),
        expiredAt: dayjs(configurationUpdateForm.launchPlannedAt)
          .add(1, "month")
          .utc()
          .toISOString(),
        scheduler: OperationScheduler.SERVICE,
        startsAtCreation: configurationUpdateForm.startsAtCreation,
        customInputParameters,
      };

      await createOperation(createOperationPayload);

      if (errorCreateOperation.value) {
        error.value = errorCreateOperation.value;
      }
    }

    if (error.value) {
      error.value.notify(NotificationKey.error);
    } else {
      showNotificationSuccess(t(NotificationKey.success));
      emits("change");
      onClose();
    }
  }
};

onMounted(loadData);

watch(
  () => props.show,
  (show) =>
    setOperationDetailForm(
      show && props.operation ? props.operation : undefined,
    ),
);
watch(
  () => props.reloadConfigurationList,
  async (reload) => {
    if (reload) {
      await loadData();
      emits("update:reloadConfigurationList", false);
    }
  },
);
</script>

<template>
  <lxc-side-canvas
    ref="sideCanvasRef"
    v-model:show="showSideCanvas"
    :header="t('dtwins.form.configuration.update.title')"
    :close-tooltip="t('filters.close')"
    size="1/3"
    :confirm-title="t('dtwins.form.configuration.update.cancel.confirm.title')"
    :confirm-message="
      t('dtwins.form.configuration.update.cancel.confirm.message')
    "
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    :confirm-enabled="formHasChanged"
    @hidden="onClose"
  >
    <container-component
      :px="0"
      :py="0"
      :is-loading="isLoadingConfigurations"
      :error="errorConfigurations"
    >
      <lxc-form
        ref="configurationUpdateFormRef"
        :model="configurationUpdateForm"
        :rules="rules"
        class="flex flex-col gap-2"
      >
        <h6>{{ t("dtwins.form.configuration.update.scheduling.title") }}</h6>
        <lxc-form-item
          :label="t('dtwins.form.configuration.update.scheduling.label')"
          prop="launchPlannedAt"
        >
          <lxc-input
            v-model="configurationUpdateForm.launchPlannedAt"
            type="datetime"
            :placeholder="
              t('dtwins.form.configuration.update.scheduling.placeholder')
            "
            prefix-read-only
            :min-date="defaultStartDate"
            class="!w-1/2"
          />
        </lxc-form-item>
        <h6>
          {{
            t("dtwins.form.configuration.update.configurationSelection.title")
          }}
        </h6>
        <lxc-form-item
          :label="
            t('dtwins.form.configuration.update.configurationSelection.label')
          "
          prop="configurationUid"
        >
          <lxc-select
            v-model="configurationUpdateForm.configurationUid"
            :placeholder="
              t(
                'dtwins.form.configuration.update.configurationSelection.placeholder',
              )
            "
            :select-no-result-text="t('input.select.search.noResults')"
            :is-loading-options="isLoadingConfigurations"
          >
            <lxc-option
              v-for="(configuration, index) in configurations"
              :key="index"
              :value="configuration.uid"
              :label="configuration.descriptor?.versionName"
              class="w-full"
            />
          </lxc-select>
        </lxc-form-item>
      </lxc-form>
    </container-component>

    <template #footer>
      <div class="grid grid-cols-[max-content_auto] gap-4">
        <lxc-button
          html-type="button"
          type="secondary"
          :title="t('button.cancel')"
          @click="onCancel"
        >
          {{ t("button.cancel") }}
        </lxc-button>
        <lxc-button
          html-type="button"
          :disabled="isConfirmDisabled"
          :title="t('button.confirm')"
          :is-loading="isLoadingCreateOperation || isLoadingUpdateOperation"
          @click="onSubmit"
        >
          {{ t("button.confirm") }}
        </lxc-button>
      </div>
    </template>
  </lxc-side-canvas>
</template>
